import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

/** Minimal, accessible form primitives. Radix is only pulled in where real
 *  behaviour is needed (focus traps, listboxes); native elements are used
 *  everywhere they already do the right thing. */

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'secondary', size = 'md', ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium',
        'transition-colors duration-150 disabled:opacity-50 disabled:pointer-events-none',
        size === 'sm' ? 'h-8 px-3 text-sm' : 'h-9 px-4 text-sm',
        variant === 'primary' &&
          'bg-[var(--accent)] text-[var(--accent-fg)] hover:opacity-90',
        variant === 'secondary' &&
          'border bg-[var(--bg-elevated)] text-fg hover:bg-[var(--bg-hover)]',
        variant === 'ghost' && 'text-muted hover:bg-[var(--bg-hover)] hover:text-fg',
        variant === 'danger' && 'bg-[var(--danger)] text-white hover:opacity-90',
        className,
      )}
      {...props}
    />
  ),
)
Button.displayName = 'Button'

export const Input = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => (
  <input
    ref={ref}
    className={cn(
      'h-9 w-full rounded-md border bg-[var(--bg)] px-3 text-sm text-fg',
      'placeholder:text-[var(--fg-subtle)] transition-colors',
      'focus:border-[var(--accent)] disabled:opacity-50',
      className,
    )}
    {...props}
  />
))
Input.displayName = 'Input'

type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  options: { value: string; label: string }[]
  placeholder?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, options, placeholder = 'Any', ...props }, ref) => (
    <select
      ref={ref}
      className={cn(
        'h-9 w-full rounded-md border bg-[var(--bg)] px-2.5 text-sm text-fg',
        'transition-colors focus:border-[var(--accent)]',
        className,
      )}
      {...props}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  ),
)
Select.displayName = 'Select'

export function Checkbox({
  label,
  checked,
  onChange,
  ...props
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'checked'>) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-muted hover:text-fg">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 rounded border accent-[var(--accent)]"
        {...props}
      />
      {label}
    </label>
  )
}

export function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={htmlFor}
        className="block text-xs font-medium tracking-wide text-subtle uppercase"
      >
        {label}
      </label>
      {children}
    </div>
  )
}
